/**
 * Security Configuration for RepConnect
 * Handles Content Security Policy and other security measures
 */

import { logger } from './logger';

/**
 * Security configuration for different environments
 */
export const securityConfig = {
  production: {
    enableCSP: true,
    strictMode: true,
    allowUnsafeInline: false,
    reportViolations: true
  },
  development: {
    enableCSP: false,
    strictMode: false,
    allowUnsafeInline: true,
    reportViolations: false
  }
};

/**
 * Get current security configuration based on environment
 */
export const getCurrentSecurityConfig = () => {
  const env = process.env.REACT_APP_ENVIRONMENT || 'development';
  return securityConfig[env] || securityConfig.development;
};

/**
 * CSP violation reporter
 */
export const handleCSPViolation = (violationEvent) => {
  const violation = violationEvent.originalEvent || violationEvent;

  logger.error('CSP Violation detected', {
    blockedURI: violation.blockedURI,
    violatedDirective: violation.violatedDirective,
    originalPolicy: violation.originalPolicy,
    sourceFile: violation.sourceFile,
    lineNumber: violation.lineNumber,
    columnNumber: violation.columnNumber
  });

  // In production, you might want to send this to a monitoring service
  if (process.env.REACT_APP_ENVIRONMENT === 'production') {
    // Example: send to monitoring service
    // analyticsService.track('csp_violation', violation);
  }
};

/**
 * Initialize security measures
 */
export const initializeSecurity = () => {
  const config = getCurrentSecurityConfig();

  logger.debug('Initializing security with config:', config);

  // Add CSP violation listener
  if (config.reportViolations && typeof window !== 'undefined') {
    window.addEventListener('securitypolicyviolation', handleCSPViolation);

    // Also listen for older browsers
    document.addEventListener('securitypolicyviolation', handleCSPViolation);
  }

  // Add additional security measures
  if (config.strictMode && typeof window !== 'undefined') {
    // Prevent eval() usage in strict mode
    window.eval = function() {
      logger.error('eval() usage blocked for security');
      throw new Error('eval() is disabled for security reasons');
    };

    // Add protection against prototype pollution
    Object.freeze(Object.prototype);
    Object.freeze(Array.prototype);
  }

  logger.info('Security initialization complete');
};

/**
 * Sanitize user input to prevent XSS
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;

  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();
};

/**
 * Validate external URLs before navigation
 */
export const validateExternalURL = (url) => {
  try {
    const urlObj = new URL(url);

    // Allow only HTTPS for external links
    if (urlObj.protocol !== 'https:') {
      logger.warn('Blocked non-HTTPS external URL:', url);
      return false;
    }

    // Block suspicious domains (you can extend this list)
    const blockedDomains = [
      'data:',
      'javascript:',
      'vbscript:',
      'file:',
      'ftp:'
    ];

    if (blockedDomains.some(domain => url.toLowerCase().startsWith(domain))) {
      logger.warn('Blocked suspicious URL:', url);
      return false;
    }

    return true;
  } catch (error) {
    logger.warn('Invalid URL format:', url, error);
    return false;
  }
};

/**
 * Secure localStorage wrapper
 */
export const secureStorage = {
  setItem: (key, value) => {
    try {
      const sanitizedKey = sanitizeInput(key);
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(sanitizedKey, serializedValue);
    } catch (error) {
      logger.error('Failed to set localStorage item:', error);
    }
  },

  getItem: (key) => {
    try {
      const sanitizedKey = sanitizeInput(key);
      const item = localStorage.getItem(sanitizedKey);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      logger.error('Failed to get localStorage item:', error);
      return null;
    }
  },

  removeItem: (key) => {
    try {
      const sanitizedKey = sanitizeInput(key);
      localStorage.removeItem(sanitizedKey);
    } catch (error) {
      logger.error('Failed to remove localStorage item:', error);
    }
  }
};

export default {
  initializeSecurity,
  sanitizeInput,
  validateExternalURL,
  secureStorage,
  getCurrentSecurityConfig
};